import React from "react";
import './ModalComponents.css';
import GamePreviewModal from "./GamePreviewModal";
import GameWatchModal from "./GameWatchModal";
import GameReminderModal from "./GameReminderModal";
import LetsPlayModal from "./LetsPlayModal";

/**
 * SimplifyModals - Aggregates all modal components for Simplify
 *
 * Props:
 * - previewGame: Game|null - Game to preview
 * - setPreviewGame: (game: null) => void
 * - watchGame: Game|null - Game to watch
 * - setWatchGame: (game: null) => void
 * - reminderGame: Game|null - Game for reminder
 * - setReminderGame: (game: null) => void
 * - showGameSelectionModal: boolean
 * - setShowGameSelectionModal: (show: boolean) => void
 * - gameSelectionMode: 'watch' | 'reminder' | 'marketplace'
 * - gameSelectionData: object
 * - onSelectGame: (game: Game) => void
 * - showLetSPlayModal: boolean
 * - setShowLetSPlayModal: (show: boolean) => void
 * - letSPlayGame: Game|null
 * - setLetSPlayGame: (game: null) => void
 * - showSessionFormInLetSPlay: boolean
 * - setShowSessionFormInLetSPlay: (show: boolean) => void
 * - showRulesSummaryInLetSPlay: boolean
 * - setShowRulesSummaryInLetSPlay: (show: boolean) => void
 * - showScorePadInLetSPlay: boolean
 * - setShowScorePadInLetSPlay: (show: boolean) => void
 * - onLogSession: () => void
 * - onSeeRulesSummary: () => void
 * - onOpenScorePad: () => void
 * - sessionData: object
 * - setSessionData: (data: object) => void
 * - onSaveSession: () => void
 * - showSessionModal: boolean
 * - user: object
 * - showBanner: (message: string, type?: string) => void
 * - onSearchSimilar: (game: Game) => void - Handler for Search Similar button
 * - onCompare: (game: Game) => void - Handler for Compare button
 * - onAddToMarketplace: (game: Game) => void - Handler for Find a copy button
 * - onAddWatch: (game: Game) => void - Handler for Watch button
 * - onAddReminder: (game: Game) => void - Handler for Reminder button
 * - onLetSPlay: (game: Game) => void - Handler for Let's Play button
 */

const SimplifyModals = ({
  // Preview
  previewGame,
  setPreviewGame,

  // Watch
  watchGame,
  setWatchGame,

  // Reminder
  reminderGame,
  setReminderGame,

  // Game Selection
  showGameSelectionModal,
  setShowGameSelectionModal,
  gameSelectionMode,
  gameSelectionData,
  onSelectGame,

  // Let's Play
  showLetSPlayModal,
  setShowLetSPlayModal,
  letSPlayGame,
  setLetSPlayGame,
  showSessionFormInLetSPlay,
  setShowSessionFormInLetSPlay,
  showRulesSummaryInLetSPlay,
  setShowRulesSummaryInLetSPlay,
  showScorePadInLetSPlay,
  setShowScorePadInLetSPlay,
  onLogSession,
  onSeeRulesSummary,
  onOpenScorePad,
  sessionData,
  setSessionData,
  onSaveSession,

  // Session
  _showSessionModal,
  user,
  userMap,
  fetchUserInfo,
  showBanner,
  // Game Preview Modal Handlers
  onSearchSimilar,
  onCompare,
  onAddToMarketplace,
  onAddWatch,
  onAddReminder,
  onLetSPlay,
  onTogglePlayList,
  inPlayList
}) => {
  return (
    <>
      {/* Game Preview Modal */}
      {previewGame && (
        <GamePreviewModal
          game={previewGame}
          onClose={() => setPreviewGame(null)}
          onSearchSimilar={onSearchSimilar}
          onCompare={onCompare}
          onAddToMarketplace={onAddToMarketplace}
          onAddWatch={onAddWatch}
          onAddReminder={onAddReminder}
          onLetSPlay={onLetSPlay}
          letsPlayConfig={{
            onLogSession: (game) => {
              setLetSPlayGame(game);
              setShowLetSPlayModal(true);
              setShowSessionFormInLetSPlay(true);
              setShowRulesSummaryInLetSPlay(false);
              setShowScorePadInLetSPlay(false);
            },
            onOpenScorePad: (game) => {
              setLetSPlayGame(game);
              setShowLetSPlayModal(true);
              setShowScorePadInLetSPlay(true);
              setShowSessionFormInLetSPlay(false);
              setShowRulesSummaryInLetSPlay(false);
            },
          }}
          user={user}
          onTogglePlayList={onTogglePlayList}
          inPlayList={inPlayList(previewGame?.id || previewGame?.game_id)}
        />
      )}

      {/* Watch Game Modal */}
      {watchGame && (
        <GameWatchModal
          game={watchGame}
          onClose={() => setWatchGame(null)}
        />
      )}

      {/* Reminder Modal */}
      {reminderGame && (
        <GameReminderModal
          game={reminderGame}
          onClose={() => setReminderGame(null)}
        />
      )}

      {/* Game Selection Modal */}
      {showGameSelectionModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowGameSelectionModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>
              Select Game to {gameSelectionMode === 'watch' ? 'Watch' : gameSelectionMode === 'reminder' ? 'Set Reminder' : 'Marketplace'}
            </h3>
            {/* Game selection list would go here - passed as children or data */}
            <div className="game-selection-list">
              {gameSelectionData?.games?.map((game) => (
                <div
                  key={game.id}
                  className="game-selection-item"
                  onClick={() => onSelectGame(game)}
                >
                  {game.name}
                </div>
              ))}
            </div>
            <button onClick={() => setShowGameSelectionModal(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Let's Play Modal */}
      <LetsPlayModal
        isOpen={showLetSPlayModal}
        game={letSPlayGame}
        user={user}
        onClose={() => {
          setShowLetSPlayModal(false);
          setLetSPlayGame(null);
          setShowSessionFormInLetSPlay(false);
          setShowRulesSummaryInLetSPlay(false);
          setShowScorePadInLetSPlay(false);
        }}
        showSessionForm={showSessionFormInLetSPlay}
        showRulesSummary={showRulesSummaryInLetSPlay}
        showScorePad={showScorePadInLetSPlay}
        onLogSession={() => onLogSession(letSPlayGame)}
        onSeeRulesSummary={onSeeRulesSummary}
        onOpenScorePad={onOpenScorePad}
        onCloseScorePad={() => setShowScorePadInLetSPlay(false)}
        onLogSessionFromScorePad={(data) => {
          setSessionData(data);
          setShowScorePadInLetSPlay(false);
          setShowSessionFormInLetSPlay(true);
        }}
        sessionData={sessionData}
        setSessionData={setSessionData}
        onSaveSession={onSaveSession}
        showBanner={showBanner}
        userMap={userMap}
        fetchUserInfo={fetchUserInfo}
        source="simplify"
      />
    </>
  );
};

export default SimplifyModals;
